import { defineFeature } from '../define-feature';
import { TEST_IDS, fieldTestId, sidebarNavTestId, sidebarSubTestId } from '../test-ids';

export default defineFeature({
  id: 'marketing.blog.article.create',
  category: 'marketing',
  title: 'Créer et publier un article de blog',
  intent:
    'Rédiger un article optimisé pour le référencement (titre, contenu, résumé, images, auteur, catégories) et le publier sur votre site, avec une diffusion possible dans vos actualités Google.',
  context: {
    why: "Le blog est l'un des leviers de référencement les plus puissants à votre portée. Chaque article répond à une question que se posent vos clients, se positionne sur des mots-clés, et fait venir du trafic sur votre site — durablement, longtemps après sa publication. Un contenu frais, riche et bien structuré signale à Google que votre site est vivant et fait autorité sur votre sujet. Trois éléments font la différence : la structure (un titre principal clair, des sous-titres qui découpent le propos), les mots-clés placés naturellement dans ces titres et le texte, et l'auteur — car les moteurs regardent qui écrit. Un article signé par un membre de votre équipe, dont le profil est cohérent avec le sujet, pèse davantage qu'un texte anonyme. Pulse vous aide à écrire un titre, un contenu et un résumé bien construits, que vous relisez et ajustez avant de publier.",
    use_cases: [
      'Publier un article « conseils » qui répond aux questions récurrentes de vos clients et attire du trafic.',
      'Diffuser une nouveauté à la fois sur votre site et dans vos actualités Google.',
      'Rédiger le contenu avec Pulse, puis relire et ajuster à votre voix.',
      "Programmer un article pour qu'il paraisse à une date choisie.",
    ],
  },
  required_scopes: ['website.news.create', 'website.news.update'],
  ui_entry_points: [{ route: '/marketing/publications', element: 'Bouton « Créer une publication »' }],
  api: {
    primary_operation_id: 'ProNewsAppController_create',
    related_operation_ids: [
      'ProNewsAppController_update',
      'ProNewsAppController_publish',
      'ProNewsAppController_unpublish',
      'ProNewsAppController_schedule',
      'ProNewsAppController_unschedule',
      'ProNewsAppController_list',
      'ProNewsAppController_get',
      'ProNewsAppController_delete',
    ],
  },
  steps: [
    '**Ouvrez les publications** depuis Communication, puis cliquez sur « Créer une publication ».',
    "**Saisissez le titre** — c'est le titre principal de l'article, et le premier signal de référencement. Pulse peut vous en proposer un.",
    '**Rédigez le contenu** structuré en sous-titres, avec vos mots-clés placés naturellement. Pulse aide à produire un texte long et bien découpé.',
    "**Ajoutez un résumé, des images, et choisissez l'auteur** parmi les membres de votre équipe : un auteur crédible renforce l'autorité de l'article.",
    "**Rangez l'article dans une ou plusieurs catégories** pour l'organiser et aider le référencement.",
    "**Activez la diffusion sur Google** si vous voulez que l'article apparaisse aussi dans vos actualités Google.",
    '**Publiez maintenant, ou programmez** la parution à une date future.',
  ],
  gotchas: [
    "**Structure de référencement.** Le titre est le titre principal (un seul par article) ; le contenu se découpe en sous-titres, et c'est dans ces sous-titres et le texte que vos mots-clés doivent apparaître naturellement. Un article bien structuré est plus lisible pour vos clients ET mieux compris par Google.",
    "**Choisir un auteur renforce le référencement.** Les moteurs évaluent le profil de l'auteur : un membre d'équipe avec un profil actif et cohérent avec le sujet ajoute de l'autorité à l'article. C'est pour cela que l'auteur se choisit parmi votre équipe, et non au hasard.",
    "**L'adresse de l'article se remplit toute seule.** Elle est dérivée du titre ; vous pouvez la personnaliser si besoin. Évitez de la changer une fois l'article référencé, pour ne pas casser les liens existants.",
    "**Diffusion Google.** Activer cette option publie aussi l'article dans vos actualités Google, ce qui renforce votre visibilité locale. Elle suppose que votre fiche Google est synchronisée.",
    "**Publier ou programmer.** Vous pouvez publier tout de suite, ou fixer une date de parution — pratique pour préparer plusieurs articles à l'avance et lisser votre présence dans le temps. Un article publié peut être dépublié à tout moment.",
    "**Pulse est un point de départ, pas un livrable final.** Il propose un titre, un contenu et un résumé structurés ; relisez et ajustez toujours pour y mettre votre voix, vos exemples et votre expertise réelle — c'est ce qui distingue un article générique d'un article qui vous ressemble.",
  ],
  related_features: [
    'marketing.blog.category.manage',
    'establishment.team.profile',
    'establishment.google_sync.connect',
    'website.builder.pages',
  ],
  demo_script: {
    duration_target_s: 45,
    steps: [
      {
        action: 'navigate',
        value: '/fr-FR',
        caption: 'Le blog attire durablement du trafic vers votre site — voyons comment publier un article.',
      },
      {
        action: 'click',
        target: sidebarNavTestId('/marketing'),
        caption: 'Dans le menu latéral, ouvrez « Communication ».',
      },
      { action: 'click', target: sidebarSubTestId('/marketing/publications'), caption: 'Puis « Blog ».' },
      { action: 'click', target: TEST_IDS.publications_create, caption: 'Créez une publication.', capture: true },
      { action: 'wait_for', target: fieldTestId('title'), caption: 'L’éditeur d’article s’ouvre.' },
      {
        action: 'fill',
        target: fieldTestId('title'),
        value: '5 conseils pour bien préparer votre séance',
        caption: 'Le titre est le premier signal de référencement ; Pulse peut vous en proposer un.',
      },
      {
        action: 'fill',
        target: fieldTestId('excerpt'),
        value: 'Nos conseils pour arriver serein et profiter pleinement de votre rendez-vous.',
        caption: 'Ajoutez un résumé, qui donne envie de lire et aide à vous référencer.',
        capture: true,
      },
      {
        action: 'click',
        target: TEST_IDS.dynamic_form_submit,
        caption: 'On enregistre : l’article est prêt à publier, tout de suite ou à la date de votre choix.',
      },
    ],
  },
});

// 💬 Verbatim Kaltouma
// > (Vocal Kaltouma.) Créer une publication (haut à droite) : titre, slug (auto, forçable), contenu,
// > résumé, images, auteur, catégorie, option publier sur Google My Business (actualités). Pulse aide
// > sur titre/contenu/résumé pour un contenu structuré et optimisé SEO. Recos SEO : titre = H1,
// > contenu H2 + H3, mots-clés dans H2/H3, 1000-1500 mots. Auteur important pour l'autorité (les
// > moteurs regardent le profil : Insta, Facebook, expertise) → renforce le référencement.
